// ============================================================
// LearningModule.tsx — 自習課程模組元件
// 顯示四大學習模組，支援進度追蹤、知識卡片、互動測驗
// ============================================================

import React, { FC, useState } from 'react';
import { LearningModule as ILearningModule, QuizQuestion } from 'types/posture';

// ── 長者友善設計常數 ──────────────────────────────────────────
// 背景 rgb(10,10,26)，所有文字對比均遵循 WCAG AA（4.5:1 以上）
const COLORS = {
    bg:            'rgb(10, 10, 26)',
    cardBg:        'rgba(255, 255, 255, 0.08)',
    cardBgDone:    'rgba(0, 255, 136, 0.15)',
    borderDefault: 'rgba(255, 255, 255, 0.25)',   // 比 #333 亮很多
    borderDone:    '#00FF88',
    borderActive:  '#60C8FF',

    textPrimary:   '#FFFFFF',                     // 主標題
    textSecondary: '#C8D8E8',                     // 副標題、說明（偏藍白）
    textMuted:     '#9AAFBF',                     // 輔助資訊（時間/主題數）
    textContent:   '#E0EAF0',                     // 內文（接近白）

    accent:        '#60C8FF',                     // 互動藍（比 #00AAFF 更亮）
    success:       '#00FF88',
    error:         '#FF6B6B',                     // 比 #FF4444 更柔和但對比更高
    explanation:   '#7FFFB0',                     // 解析文字（淡綠）
};

// ── 靜態課程資料 ────────────────────────────────────────
const LEARNING_MODULES: ILearningModule[] = [
    {
        id:          'module_1_basics',
        title:       'Module 1：姿勢科學基礎',
        titleJP:     '姿勢の基礎知識',
        description: '了解脊椎結構、重力中心線及肌肉失衡原理，建立正確的身體認知',
        duration:    15,
        topics: [
            {
                id:      'spine_anatomy',
                title:   '脊椎自然曲線',
                content: `脊椎有三個自然曲線：
• 頸椎前凸（Cervical Lordosis）：向前彎曲 20-40°
• 胸椎後凸（Thoracic Kyphosis）：向後彎曲 20-45°
• 腰椎前凸（Lumbar Lordosis）：向前彎曲 30-50°

這三個曲線如彈簧般協同分散身體重量與衝擊力。
姿勢不良會使曲線異常，大幅增加椎間盤和肌肉的負擔。`,
            },
            {
                id:      'gravity_line',
                title:   '重力中心線',
                content: `理想站姿中，從側面看：
耳朵 → 肩峰 → 大轉子（髖骨） → 膝蓋略前 → 外踝前方

應成一條近乎垂直的重力中心線。
偏離此線越遠，相關肌肉需要出越多力維持平衡，造成疲勞和疼痛。`,
            },
            {
                id:      'breathing',
                title:   '呼吸與姿勢的關係',
                content: `橫膈膜是核心穩定的關鍵肌肉。
正確腹式呼吸能增加腹內壓，自然支撐腰椎，減少背痛風險。

練習方法：
1. 一手放胸口，一手放腹部
2. 吸氣時腹部膨起（非胸口）
3. 呼氣時腹部自然回縮，輕微收緊核心`,
            },
        ],
        quizQuestions: [
            {
                id:           'q1',
                question:     '理想站姿中，以下哪個身體部位應該在同一條垂直線上？',
                options:      ['鼻子、肚臍、腳趾', '耳朵、肩膀、髖骨、踝骨', '眼睛、膝蓋、腳跟', '下巴、肩膀、膝蓋'],
                correctIndex: 1,
                explanation:  '正確！從側面看，耳朵→肩峰→髖骨大轉子→踝骨前方應成一直線，這是重力中心線的標準位置。',
            },
            {
                id:           'q2',
                question:     '頸椎每前傾 2.5cm，大約增加多少頸椎承重？',
                options:      ['1-2 kg', '4-5 kg', '8-10 kg', '15 kg 以上'],
                correctIndex: 1,
                explanation:  '根據 Hansraj（2014）的研究，頭部每向前傾斜 2.5cm，頸椎額外承受約 4-5kg 的壓力，長期下來會加速頸椎退化。',
            },
        ],
    },
    {
        id:          'module_2_service_posture',
        title:       'Module 2：前台服務站姿',
        titleJP:     'おもてなしの立ち姿',
        description: '掌握日本商場服務標準的站姿規範，從お辞儀角度到手部擺放',
        duration:    20,
        topics: [
            {
                id:      'basic_stance',
                title:   '基本立姿（基本立ち）',
                content: `標準前台站姿要領：
• 腳跟靠攏或微開 15°（V 字型）
• 膝蓋微鬆，不鎖死（保持 5-10° 微彎）
• 骨盆中立，微收下腹（核心輕啟動）
• 肩膀水平放鬆，向後向下沉
• 下巴水平，視線略低於客人視線
• 表情自然微笑（顴肌輕啟動）`,
            },
            {
                id:      'ojigi',
                title:   'お辞儀（鞠躬）標準',
                content: `鞠躬角度根據場合有嚴格區分：
• 15°（会釈/Eshaku）：日常打招呼、通道相遇
• 30°（普通礼/Futsurei）：歡迎客人、感謝購買
• 45°（最敬礼/Saikeirei）：深度道歉、迎接 VIP

鞠躬要領：
✓ 從髖關節開始彎曲（非腰部）
✓ 後背保持平直，頸椎延伸
✓ 眼睛向下看地面前方約 1.5m 處
✓ 速度：彎下稍快，起身稍慢`,
            },
        ],
        quizQuestions: [
            {
                id:           'q3',
                question:     '客人購物後結帳，表示感謝的鞠躬角度應為？',
                options:      ['5-10°', '15°', '30°', '45°'],
                correctIndex: 2,
                explanation:  '30° 的「普通礼」適用於歡迎、感謝等正式服務場合。45° 的「最敬礼」則用於深度道歉或迎接最高貴賓。',
            },
        ],
    },
    {
        id:          'module_3_standing_health',
        title:       'Module 3：久站護理',
        titleJP:     '長時間立ち仕事の健康管理',
        description: '學習減輕久站身體負擔的技巧，包含體重轉移、足弓保護及崗位伸展',
        duration:    15,
        topics: [
            {
                id:      'weight_transfer',
                title:   '體重轉移技巧',
                content: `長時間站立建議：
• 每 20-30 分鐘有意識地換腳重心
• 使用 Anti-Fatigue 站立墊（可減少下肢疲勞 50%）
• 避免雙腳完全並攏站立（降低靜脈回流效率）
• 條件允許時使用腳踏凳輪流擱腳`,
            },
            {
                id:      'footwear',
                title:   '工作鞋選擇建議',
                content: `前台工作鞋標準：
• 跟高：2-3cm（過高增加腰椎壓力，過平減少足弓支撐）
• 足弓支撐：有明顯中足支撐設計
• 前掌空間：腳趾可自由活動
• 材質：透氣，不造成足部濕熱
• 建議每 6-8 個月更換（避免緩衝性能下降）`,
            },
        ],
        quizQuestions: [
            {
                id:           'q4',
                question:     '以下哪種方法最有效減輕前台久站疲勞？',
                options:      ['完全不動保持標準站姿', '每 20-30 分鐘輕微轉移重心或小踏步', '坐下休息', '靠牆站立'],
                correctIndex: 1,
                explanation:  '規律的體重轉移能激活腿部肌肉泵，促進靜脈回流，有效預防下肢水腫和靜脈曲張。',
            },
        ],
    },
    {
        id:          'module_4_correction',
        title:       'Module 4：常見問題矯正',
        titleJP:     'よくある姿勢の問題と改善',
        description: '針對最常見的四種姿勢問題：頭部前傾、圓肩、骨盆前傾、重心偏移，提供系統矯正方案',
        duration:    25,
        topics: [
            {
                id:      'text_neck',
                title:   '頭部前傾矯正',
                content: `收下巴練習（Chin Tuck）步驟：
1. 站立或坐直，視線朝前
2. 在不點頭的情況下，將頭部向後平移
3. 感受後頸部有輕微拉伸感
4. 保持 5 秒後放鬆
5. 重複 10 次，每天 3 組

注意：這是「水平後移」而非「低頭」，區別很重要！`,
            },
        ],
        quizQuestions: [
            {
                id:           'q5',
                question:     '「收下巴練習（Chin Tuck）」的正確動作是？',
                options:      ['將頭向下點', '讓頭部水平向後平移', '將下巴抬高', '左右轉動頸部'],
                correctIndex: 1,
                explanation:  '正確的 Chin Tuck 是頭部水平向後平移，不是低頭，這個動作能強化頸部深層屈肌，矯正頭部前傾。',
            },
        ],
    },
];

// ── 主要元件 ──────────────────────────────────────────────
interface LearningModuleProps {
    staffId:          number;
    completedModules: string[];
    onModuleComplete: (moduleId: string, quizScore: number) => void;
}

const LearningModuleView: FC<LearningModuleProps> = ({
                                                         staffId,
                                                         completedModules,
                                                         onModuleComplete,
                                                     }) => {
    const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
    const [activeTopicIdx, setActiveTopicIdx] = useState<number>(0);
    const [quizMode, setQuizMode]             = useState<boolean>(false);
    const [answers, setAnswers]               = useState<number[]>([]);
    const [submitted, setSubmitted]           = useState<boolean>(false);

    const activeModule = LEARNING_MODULES.find(m => m.id === activeModuleId);

    // ── 測驗提交處理 ────────────────────────────────────────
    function handleQuizSubmit(): void {
        if (!activeModule) return;

        const score = Math.round(
            (answers.filter((ans, idx) => ans === activeModule.quizQuestions[idx]?.correctIndex).length /
                activeModule.quizQuestions.length) * 100
        );

        setSubmitted(true);
        if (score >= 60) {
            // 通過測驗（60分以上），標記完成
            onModuleComplete(activeModule.id, score);
        }
    }

    // ── 模組選擇畫面 ───────────────────────────────────────
    if (!activeModuleId) {
        return (
            <div className="learning-modules-grid">
                <h2 style={{
                    color:        COLORS.textPrimary,
                    fontSize:     22,               // ↑ 比原本 16 大
                    fontWeight:   700,
                    marginBottom: 24,
                    letterSpacing: 0.5,
                }}>📚 自習學習中心</h2>
                <div style={{
                    display:             'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap:                 20,
                }}>
                    {LEARNING_MODULES.map((module, idx) => {
                        const isCompleted = completedModules.includes(module.id);
                        return (
                            <div
                                key={module.id}
                                onClick={() => {
                                    setActiveModuleId(module.id);
                                    setActiveTopicIdx(0);
                                    setQuizMode(false);
                                    setSubmitted(false);
                                    setAnswers([]);
                                }}
                                style={{
                  background:   isCompleted ? COLORS.cardBgDone : COLORS.cardBg,
                                    border:       `2px solid ${isCompleted ? COLORS.borderDone : COLORS.borderDefault}`,
                  borderRadius: '16px',
                  padding:      '24px',
                                    cursor:       'pointer',
                                    transition:   'all 0.2s ease',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 32 }}>          {/* ↑ 圖示放大 */}
          {['🦴', '🏬', '🦵', '🔧'][idx]}
        </span>
                                    {isCompleted && (
                    <span style={{
                      color:      COLORS.success,
                      fontSize:   22,
                      fontWeight: 700,
                    }}>✓</span>
                                    )}
                                </div>

                {/* 主標題 */}
                <h3 style={{
                  color:      COLORS.textPrimary,
                  fontSize:   16,            // ↑ 原本 14
                  fontWeight: 700,
                  margin:     '14px 0 6px',
                  lineHeight: 1.4,
                }}>
                                    {module.title}
                                </h3>

                {/* 日文副標題 */}
                <p style={{
                  color:      COLORS.textSecondary,
                  fontSize:   13,            // ↑ 原本 12
                  margin:     0,
                  lineHeight: 1.5,
                }}>
                                    {module.titleJP}
                                </p>

                {/* 時間/主題 */}
                <p style={{
                  color:      COLORS.textMuted,
                  fontSize:   12,            // ↑ 原本 11
                  margin:     '10px 0 0',
                  fontWeight: 500,
                }}>
                                    ⏱ {module.duration} 分鐘 · {module.topics.length} 個主題
                                </p>
                            </div>
                        );
                    })}

                </div>
            </div>
        );
    }

    // ── 模組內容畫面 ───────────────────────────────────────
    if (!activeModule) return null;

    return (
        <div className="module-content">
            {/* 標題列 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <button onClick={() => setActiveModuleId(null)}
                        style={{
                        background:   'rgba(255,255,255,0.1)',
                        border:       `1px solid ${COLORS.borderDefault}`,
                        borderRadius: '8px',
                        color:        COLORS.accent,
                        cursor:       'pointer',
                        fontSize:     22,
                        padding:      '6px 14px',
                        lineHeight:   1,
                      }}
                    >
                    ←
                </button>
                <div>
          <h2 style={{
            color:      COLORS.textPrimary,
            margin:     0,
            fontSize:   20,              // ↑ 原本 16
            fontWeight: 700,
          }}>
            {activeModule.title}
          </h2>
          <p style={{
            color:    COLORS.textSecondary,
            margin:   '4px 0 0',
            fontSize: 14,              // ↑ 原本 12
          }}>
            {activeModule.titleJP}
          </p>
                </div>
            </div>

            {!quizMode ? (
                <>
                    {/* 主題標籤頁 */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        {activeModule.topics.map((topic, idx) => (
                            <button
                                key={topic.id}
                                onClick={() => setActiveTopicIdx(idx)}
                                style={{
                                  padding:      '10px 18px',        // ↑ 更大點擊區域
                                  borderRadius: '24px',
                                  border:       `2px solid ${activeTopicIdx === idx ? COLORS.borderActive : COLORS.borderDefault}`,
                                  background:   activeTopicIdx === idx ? 'rgba(96,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                                  color:        activeTopicIdx === idx ? COLORS.accent : COLORS.textSecondary,
                                    cursor:     'pointer',
                                  fontSize:     14,                 // ↑ 原本 12
                                  fontWeight:   activeTopicIdx === idx ? 700 : 400,
                                  transition:   'all 0.2s',
                                }}
                            >
                                {topic.title}
                            </button>
                        ))}
                    </div>

                    {/* 主題內容 */}
                    <div style={{
            background:   'rgba(255,255,255,0.07)',
            border:       `1px solid ${COLORS.borderDefault}`,
            borderRadius: '14px',
            padding:      '24px',
            marginBottom: '20px',
                        whiteSpace:   'pre-line',
            color:        COLORS.textContent,    // ↑ 原本 #ddd
            lineHeight:   2.0,                   // ↑ 更寬鬆
            fontSize:     16,                    // ↑ 原本 14
                    }}>
            <h3 style={{
              color:      COLORS.textPrimary,
              marginTop:  0,
              fontSize:   18,                    // ↑ 原本未設定
              fontWeight: 700,
            }}>
                            {activeModule.topics[activeTopicIdx]?.title}
                        </h3>
                        {activeModule.topics[activeTopicIdx]?.content}
                    </div>

                    {/* 進入測驗按鈕 */}
                    <button
                        onClick={() => setQuizMode(true)}
                        style={{
                            width:        '100%',
              padding:      '18px',              // ↑ 原本 14px
              background:   'linear-gradient(135deg, #0066cc, #60C8FF)',
                            border:       'none',
              borderRadius: '12px',
              color:        '#FFFFFF',
              fontSize:     18,                  // ↑ 原本 14
              fontWeight:   700,
                            cursor:       'pointer',
              letterSpacing: 0.5,
                        }}
                    >
                        📝 開始知識測驗（共 {activeModule.quizQuestions.length} 題）
                    </button>
                </>
            ) : (
                // ── 測驗畫面 ──────────────────────────────────────
                <div className="quiz-section">
                    {activeModule.quizQuestions.map((q, qIdx) => (
                        <div key={q.id} style={{
                            background:   'rgba(255,255,255,0.07)',
                            border:       `1px solid ${COLORS.borderDefault}`,
                            borderRadius: '14px',
                            padding:      '20px',
                            marginBottom: '16px',
                        }}>
                            <p style={{
                color:      COLORS.textPrimary,
                fontWeight: 700,
                marginTop:  0,
                fontSize:   16,                  // ↑ 原本未設定
                lineHeight: 1.6,
                            }}>
                                Q{qIdx + 1}. {q.question}
                            </p>

              {/* 選項 */}
                            {q.options.map((opt, optIdx) => {
                let borderColor = COLORS.borderDefault;
                let bgColor     = 'rgba(255,255,255,0.04)';
                let textColor   = COLORS.textSecondary;

                                if (submitted) {
                                    if (optIdx === q.correctIndex) {
                    borderColor = COLORS.success;
                    bgColor     = 'rgba(0,255,136,0.15)';
                    textColor   = '#FFFFFF';
                                    } else if (answers[qIdx] === optIdx && optIdx !== q.correctIndex) {
                    borderColor = COLORS.error;
                    bgColor     = 'rgba(255,107,107,0.15)';
                    textColor   = '#FFCCCC';
                                    }
                                } else if (answers[qIdx] === optIdx) {
                  borderColor = COLORS.accent;
                  bgColor     = 'rgba(96,200,255,0.18)';
                  textColor   = '#FFFFFF';
                                }

                                return (
                                    <div
                                        key={optIdx}
                                        onClick={() => {
                                            if (!submitted) {
                                                const newAnswers = [...answers];
                                                newAnswers[qIdx] = optIdx;
                                                setAnswers(newAnswers);
                                            }
                                        }}
                                        style={{
                      border:       `2px solid ${borderColor}`,
                                            background:   bgColor,
                      borderRadius: '10px',
                      padding:      '14px 18px',   // ↑ 更大
                      margin:       '8px 0',
                                            cursor:       submitted ? 'default' : 'pointer',
                      color:        textColor,
                      fontSize:     15,             // ↑ 原本 13
                      lineHeight:   1.6,
                                            transition:   'all 0.2s',
                      fontWeight:   answers[qIdx] === optIdx ? 600 : 400,
                                        }}
                                    >
                                        {opt}
                                    </div>
                                );
                            })}
                            {submitted && (
                                <p style={{
                  color:      COLORS.explanation,
                  fontSize:   14,                  // ↑ 原本 12
                  margin:     '12px 0 0',
                  lineHeight: 1.7,
                  fontWeight: 500,
                                }}>
                                    💡 {q.explanation}
                                </p>
                            )}
                        </div>
                    ))}

          {/* 提交 / 成績 */}
                    {!submitted ? (
                        <button
                            onClick={handleQuizSubmit}
                            disabled={answers.length < activeModule.quizQuestions.length}
                            style={{
                width:        '100%',
                padding:      '18px',
                                background: answers.length < activeModule.quizQuestions.length
                  ? 'rgba(255,255,255,0.1)'
                  : 'linear-gradient(135deg, #0066cc, #60C8FF)',
                border:       `2px solid ${answers.length < activeModule.quizQuestions.length
                  ? COLORS.borderDefault : 'transparent'}`,
                borderRadius: '12px',
                color:        answers.length < activeModule.quizQuestions.length
                  ? COLORS.textMuted : '#FFFFFF',
                fontSize:     18,
                fontWeight:   700,
                cursor:       answers.length < activeModule.quizQuestions.length
                  ? 'not-allowed' : 'pointer',
                            }}
                        >
              ✅ 提交答案
                        </button>
                    ) : (
                        <div style={{
              background:   'rgba(0,255,136,0.12)',
              border:       `2px solid ${COLORS.success}`,
              borderRadius: '12px',
              padding:      '24px',
              textAlign:    'center',
                        }}>
                            <p style={{
                color:      COLORS.success,
                fontWeight: 700,
                fontSize:   22,             // ↑ 原本 16
                margin:     '0 0 16px',
                            }}>
                                {answers.filter((a, i) => a === activeModule.quizQuestions[i]?.correctIndex).length} /
                                {activeModule.quizQuestions.length} 題答對 ✓
                            </p>
                            <button
                                onClick={() => setActiveModuleId(null)}
                                style={{
                  padding:      '14px 32px',
                  background:   COLORS.accent,
                  border:       'none',
                  borderRadius: '10px',
                  color:        '#000000',         // 深色文字在亮背景上對比最高
                  cursor:       'pointer',
                  fontSize:     16,
                  fontWeight:   700,
                                }}
                            >
                                返回課程列表
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LearningModuleView;
